import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

function LoginPage() {
    // useState stores form values that change as the user types
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { login, googleLogin } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        // prevent the default browser form submission which reloads the page
        e.preventDefault();

        // basic input sanitisation — trim whitespace
        const cleanEmail = email.trim().toLowerCase();
        const cleanPassword = password.trim();

        if (!cleanEmail || !cleanPassword) {
            setError(t('auth.errors.email_password_required'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            await login(cleanEmail, cleanPassword);
            // redirect to dashboard on success
            navigate('/dashboard');
        } catch (err) {
            setError(t('auth.errors.invalid_credentials'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        if (!credentialResponse?.credential) {
            setError('Google login failed. Try again.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await googleLogin(credentialResponse.credential);
            navigate('/dashboard');
        } catch (err) {
            const backendError = err?.response?.data?.error;
            setError(backendError || 'Google login failed. Try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <SEO
                title="Sign In"
                description="Sign in to Career Platform to access AI-powered resume analysis, interview prep, job search, and career coaching tools."
                path="/login"
            />
            <div className="auth-card">
                <h1>{t('app.name')}</h1>
                <h2>{t('auth.login')}</h2>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{t('auth.email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('auth.placeholders.email')}
                            required
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('auth.password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('auth.placeholders.password')}
                            required
                            autoComplete="current-password"
                        />
                    </div>
                    <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                        <Link to="/forgot-password" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>
                            {t('auth.forgot_password')}
                        </Link>
                    </div>

                    <button type="submit" disabled={loading}>
                        {loading ? t('auth.signing_in') : t('auth.sign_in')}
                    </button>
                </form>

                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError('Google login failed. Try again.')}
                    />
                </div>

                <p className="auth-link">
                    {t('auth.no_account')} <Link to="/register">{t('auth.create_account_cta')}</Link>
                </p>
            </div>
        </div>
    );
}

export default LoginPage;